import { Activity } from '../../types/dashboard/ActivityFeed';
import { ProfileViewsData, ProfileView } from '../../types/dashboard/ProfileViews';
import { logger } from '../../utils/logger';

// Mock data for testing components before backend is ready
export const mockActivityData: Activity[] = [
  {
    id: '1',
    type: 'profile_view',
    title: 'Someone viewed your profile',
    description: 'Alex Johnson viewed your profile',
    timestamp: new Date(Date.now() - 5 * 60 * 1000), // 5 minutes ago
    user: {
      id: 'user1',
      name: 'Alex Johnson',
      photo: '/placeholder-user.jpg'
    },
    metadata: {
      isRead: false,
      priority: 'medium'
    }
  },
  {
    id: '2',
    type: 'like_received',
    title: 'You received a like!',
    description: 'Sarah Miller liked your profile',
    timestamp: new Date(Date.now() - 15 * 60 * 1000), // 15 minutes ago
    user: {
      id: 'user2',
      name: 'Sarah Miller',
      photo: '/placeholder-user.jpg'
    },
    metadata: {
      isRead: false,
      priority: 'high',
      actionUrl: '/matches'
    }
  },
  {
    id: '3',
    type: 'match',
    title: 'It\'s a match!',
    description: 'You matched with Jordan Smith',
    timestamp: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
    user: {
      id: 'user3',
      name: 'Jordan Smith',
      photo: '/placeholder-user.jpg'
    },
    metadata: {
      isRead: true,
      priority: 'high',
      actionUrl: '/messages'
    }
  },
  {
    id: '4',
    type: 'message',
    title: 'New message',
    description: 'Taylor sent you a message',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
    user: {
      id: 'user4',
      name: 'Taylor',
      photo: '/placeholder-user.jpg'
    },
    metadata: {
      isRead: true,
      priority: 'medium',
      actionUrl: '/messages'
    }
  },
  {
    id: '5',
    type: 'super_like',
    title: 'Super like received!',
    description: 'Casey gave you a super like',
    timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
    user: {
      id: 'user5',
      name: 'Casey',
      photo: '/placeholder-user.jpg'
    },
    metadata: {
      isRead: false,
      priority: 'high',
      actionUrl: '/matches'
    }
  }
];

export const mockProfileViewsData: ProfileViewsData = {
  totalViews: 1247,
  viewsToday: 23,
  viewsYesterday: 18,
  viewsThisWeek: 156,
  viewsThisMonth: 423,
  uniqueViewers: 892,
  lastUpdated: new Date()
};

export const mockRecentViewers: ProfileView[] = [
  {
    id: 'view1',
    viewerId: 'user1',
    viewerName: 'Alex Johnson',
    viewerPhoto: '/placeholder-user.jpg',
    viewedAt: new Date(Date.now() - 5 * 60 * 1000)
  },
  {
    id: 'view2',
    viewerId: 'user2',
    viewerName: 'Sarah Miller',
    viewerPhoto: '/placeholder-user.jpg',
    viewedAt: new Date(Date.now() - 15 * 60 * 1000)
  },
  {
    id: 'view3',
    viewerId: 'user3',
    viewerName: 'Jordan Smith',
    viewerPhoto: '/placeholder-user.jpg',
    viewedAt: new Date(Date.now() - 30 * 60 * 1000)
  },
  {
    id: 'view4',
    viewerId: 'user4',
    viewerName: 'Taylor',
    viewerPhoto: '/placeholder-user.jpg',
    viewedAt: new Date(Date.now() - 2 * 60 * 60 * 1000)
  },
  {
    id: 'view5',
    viewerId: 'user5',
    viewerName: 'Casey',
    viewerPhoto: '/placeholder-user.jpg',
    viewedAt: new Date(Date.now() - 4 * 60 * 60 * 1000)
  }
];

// Mock service functions for testing
export const mockActivityService = {
  getUserActivities: async (userId: string, limit = 20, offset = 0) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    return mockActivityData.slice(offset, offset + limit);
  },
  
  markActivityAsRead: async (activityId: string) => {
    await new Promise(resolve => setTimeout(resolve, 200));
    logger.log('Marked activity as read:', activityId);
  },
  
  getUnreadActivityCount: async (userId: string) => {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockActivityData.filter(activity => !activity.metadata?.isRead).length;
  }
};

export const mockProfileViewsService = {
  getProfileViews: async (userId: string) => {
    await new Promise(resolve => setTimeout(resolve, 300));
    return mockProfileViewsData;
  },
  
  getRecentViewers: async (userId: string, limit = 5) => {
    await new Promise(resolve => setTimeout(resolve, 300));
    return mockRecentViewers.slice(0, limit);
  },
  
  getViewsAnalytics: async (userId: string, timeframe: 'day' | 'week' | 'month') => {
    await new Promise(resolve => setTimeout(resolve, 500));
    const viewsData = generateMockViewsData(timeframe);
    return {
      timeframe,
      totalViews: mockProfileViewsData.totalViews,
      uniqueViewers: mockProfileViewsData.uniqueViewers,
      viewsByDay: viewsData.map(item => ({
        date: item.label || '',
        views: item.views
      })),
      peakViewingTime: '7-9 PM',
      demographics: {
        ageGroups: {
          '18-25': 25,
          '26-35': 45,
          '36-45': 20,
          '46+': 10
        },
        locations: {
          'New York': 30,
          'Los Angeles': 25,
          'Chicago': 20,
          'Other': 25
        }
      }
    };
  },
  
  boostProfile: async (userId: string, duration: number) => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    logger.log('Profile boosted for', duration, 'hours');
  }
};

function generateMockViewsData(timeframe: 'day' | 'week' | 'month') {
  const data = [];
  const now = new Date();
  
  if (timeframe === 'day') {
    for (let i = 23; i >= 0; i--) {
      const hour = new Date(now.getTime() - i * 60 * 60 * 1000);
      data.push({
        time: hour.getHours(),
        views: Math.floor(Math.random() * 10) + 1,
        label: `${hour.getHours()}:00`
      });
    }
  } else if (timeframe === 'week') {
    for (let i = 6; i >= 0; i--) {
      const day = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      data.push({
        time: day.getDay(),
        views: Math.floor(Math.random() * 50) + 10,
        label: day.toLocaleDateString('en-US', { weekday: 'short' })
      });
    }
  } else {
    for (let i = 29; i >= 0; i--) {
      const day = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      data.push({
        time: day.getDate(),
        views: Math.floor(Math.random() * 20) + 5,
        label: day.getDate().toString()
      });
    }
  }
  
  return data;
}
